// « PAR OÙ COMMENCER » — en tête du tableau de bord.
//
// Les études expliquent les chiffres, et une étude se cadre mieux quand on sait
// déjà où en est le produit. Deux étapes, dans l'ordre, et UN bouton principal :
// la prochaine chose à faire.
//   1. la mesure zéro (l'état des lieux du produit) ;
//   2. créer une étude.
// Les deux faites, le bloc se réduit à une ligne : « Créer une étude » reste le
// geste principal du projet.
import { useCallback, useState } from 'react';
import { useStudyPlan } from '../api/queries';
import { Button } from '../components/ui';
import { Drawer } from '../write/Drawer';
import { useRouter } from '../router';
import { Mesure0Wizard } from '../mesure0/Mesure0Wizard';
import { useProjectTodo } from '../mesure0/ProjectTodo';

export function StartHere({ projectId, projectName }: { projectId: string; projectName: string }) {
  const todo = useProjectTodo(projectId);
  // Le même compte que les chiffres en tête du tableau de bord : l'étude « mesure
  // zéro » (l'état des lieux) n'est pas une étude menée.
  const studies = useStudyPlan(projectId).data?.snapshot.studies ?? null;
  const { navigate } = useRouter();
  const [wizard, setWizard] = useState(false);
  const close = useCallback(() => setWizard(false), []);

  // Rien à proposer à qui ne peut pas écrire, ni tant que le contexte manque.
  if (!todo || studies === null) return null;
  const zeroDone = todo.context.steps.product;
  const newStudy = () => navigate(`/projet/${encodeURIComponent(projectId)}/etudes/nouvelle`);

  if (zeroDone && studies > 0) {
    return (
      <section className="start-here start-here-compact" aria-label="Prochaine étape">
        <span className="start-here-line">
          {studies} étude{studies > 1 ? 's' : ''} dans ce projet. Une nouvelle question à éclairer ?
        </span>
        <Button variant="accent" onClick={newStudy}>Créer une étude</Button>
      </section>
    );
  }

  const steps = [
    {
      n: 1,
      title: 'Faire la mesure zéro',
      text: 'L’état des lieux du produit : ce qu’il vise, qui l’utilise, ce qui fonctionne ou pas.',
      done: zeroDone,
    },
    {
      n: 2,
      title: 'Créer votre première étude',
      text: 'Entretiens, observation, test, questionnaire ou audit : pour comprendre ce que les chiffres ne disent pas.',
      done: studies > 0,
    },
  ];

  return (
    <section className="zy-card start-here" aria-labelledby="start-here-title">
      <h2 className="t-h3" id="start-here-title">Par où commencer</h2>
      <ol className="zy-stepper">
        {steps.map((s, i) => (
          <li key={s.n} {...(s.done ? { 'data-etat': 'fait' } : {})} {...(i === steps.findIndex((x) => !x.done) ? { 'aria-current': 'step' as const } : {})}>
            <strong>{s.title}</strong>
            <span className="zy-texte-muet">{s.text}</span>
          </li>
        ))}
      </ol>
      <div className="start-here-actions">
        {!zeroDone ? (
          <>
            <Button variant="accent" onClick={() => setWizard(true)}>Commencer la mesure zéro</Button>
            <Button variant="ghost" onClick={newStudy}>Créer une étude directement</Button>
          </>
        ) : (
          <Button variant="accent" onClick={newStudy}>Créer une étude</Button>
        )}
      </div>
      <Drawer open={wizard} title="Mesure zéro · état des lieux" description="Où en est le produit, et ce qu’il vise." onClose={close} wide>
        {wizard ? (
          <Mesure0Wizard projectId={projectId} projectName={projectName} context={todo.context} only="product" onClose={close} />
        ) : null}
      </Drawer>
    </section>
  );
}
